import type { Cohort, Contact } from '@efdigital/ll-addon-data';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { setAsyncHasMany } from '@efdigital/ll-admin-v2/data/async-setter';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

import { ContactPicker } from '../pickers/contact-picker.gts';

function formatContactName(contact: Contact): string {
  return [contact.title, contact.firstname, contact.lastname, contact.suffix]
    .filter(Boolean)
    .join(' ');
}

export class EditCohortContacts extends Component<EditRecordSignature<Cohort>> {
  hasEmails = (contacts: Contact[]) => {
    return contacts.some((c) => c.email);
  };

  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    if (!contacts.find((item) => item.id === contact.id)) {
      setAsyncHasMany(this.args.item, 'contacts', [...contacts, contact]);
    }
  };

  removeContact = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    setAsyncHasMany(
      this.args.item,
      'contacts',
      contacts.filter((c) => c.id !== contact.id)
    );
  };

  <template>
    <EditRecord
      @editLabel="Manage Facilitators"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{1}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Facilitators</legend>

          <p class="form-text">Facilitators are the people who will be
            responsible for delivering the program to the participants. This
            list is for display purposes only and does not grant permissions to
            the program.</p>

          <Await @promise={{@item.contacts}}>
            <:success as |contacts|>
              {{#if contacts.length}}
                <div class="list-group">
                  {{#each contacts as |contact|}}
                    <div class="list-group-item">
                      <div
                        class="d-flex justify-content-start align-items-top gap-3"
                      >
                        {{#if contact.icon}}
                          <div class="flex-shrink-0 flex-grow-0">
                            <div class="ratio ratio-1x1 w-48px">
                              <img
                                src={{contact.icon}}
                                class="object-fit-cover rounded-circle"
                                alt={{formatContactName contact}}
                                width="48"
                              />
                            </div>
                          </div>
                        {{/if}}
                        <div class="flex-grow-1">
                          <h4 class="h5 mb-1">
                            {{formatContactName contact}}
                          </h4>
                          {{#if contact.organisation}}
                            <p class="list-group-item-text mb-0 small">
                              <iconify-icon icon="ph:building"></iconify-icon>
                              {{contact.organisation}}
                            </p>
                          {{/if}}
                          {{#if contact.email}}
                            <p class="list-group-item-text mb-0 small">
                              <iconify-icon icon="ph:envelope"></iconify-icon>
                              <a
                                href="mailto:{{contact.email}}"
                              >{{contact.email}}</a>
                            </p>
                          {{/if}}
                        </div>
                        <div class="flex-shrink-0 flex-grow-0">
                          {{#unless isReadOnly}}
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              {{on "click" (fn this.removeContact contact)}}
                            >
                              <iconify-icon
                                inline
                                icon="ph:x-bold"
                              ></iconify-icon>
                              <span class="visually-hidden">Remove</span>
                            </button>
                          {{/unless}}
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>
              {{else}}
                <p>No facilitators assigned yet.</p>
              {{/if}}
            </:success>
          </Await>
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <ContactPicker @onSelect={{this.handleContactSelect}}>
            <:input as |showModal|>
              <button
                type="button"
                class="btn btn-secondary btn-sm"
                {{on "click" showModal}}
              >
                <iconify-icon
                  inline
                  icon="ph:magnifying-glass-bold"
                ></iconify-icon>
                Add Facilitator
              </button>
            </:input>
          </ContactPicker>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
